import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Button } from "./ui/button";
import { ArrowRight, ChevronDown } from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

export const Hero = () => {
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const heading2Ref = useRef(null);
  const subtitleRef = useRef(null);
  const descRef = useRef(null);
  const ctaRef = useRef(null);
  const imageRef = useRef(null);
  const overlayRef = useRef(null);
  const labelRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Initial states
      gsap.set([headingRef.current, heading2Ref.current], { 
        y: 120, 
        opacity: 0,
        skewY: 7
      });
      gsap.set(labelRef.current, { y: 30, opacity: 0 });
      gsap.set(subtitleRef.current, { y: 40, opacity: 0 });
      gsap.set(descRef.current, { y: 40, opacity: 0 });
      gsap.set(ctaRef.current, { y: 40, opacity: 0 });
      gsap.set(imageRef.current, { scale: 1.3, opacity: 0 });
      gsap.set(overlayRef.current, { scaleY: 1 });

      // Master timeline
      const tl = gsap.timeline({ delay: 0.3 });

      // Image reveal
      tl.to(overlayRef.current, {
        scaleY: 0,
        duration: 1.2,
        ease: "power4.inOut",
        transformOrigin: "top"
      })
      .to(imageRef.current, {
        scale: 1,
        opacity: 1,
        duration: 1.4,
        ease: "power3.out"
      }, "-=0.8")
      
      // Label
      .to(labelRef.current, {
        y: 0,
        opacity: 1,
        duration: 0.8,
        ease: "power3.out"
      }, "-=1")
      
      // Main headings with stagger
      .to(headingRef.current, {
        y: 0,
        opacity: 1,
        skewY: 0,
        duration: 1,
        ease: "power4.out"
      }, "-=0.6")
      .to(heading2Ref.current, {
        y: 0,
        opacity: 1,
        skewY: 0,
        duration: 1,
        ease: "power4.out"
      }, "-=0.8")
      
      // Subtitle
      .to(subtitleRef.current, {
        y: 0,
        opacity: 1,
        duration: 0.8,
        ease: "power3.out"
      }, "-=0.6")
      
      // Description
      .to(descRef.current, {
        y: 0,
        opacity: 1,
        duration: 0.8,
        ease: "power3.out"
      }, "-=0.5")
      
      // CTA buttons
      .to(ctaRef.current, {
        y: 0,
        opacity: 1,
        duration: 0.8,
        ease: "power3.out"
      }, "-=0.4");

      // Parallax on scroll
      gsap.to(imageRef.current, {
        yPercent: 20,
        ease: "none",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top top",
          end: "bottom top",
          scrub: true
        }
      });

    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      ref={sectionRef}
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden bg-background"
    >
      {/* Grid Pattern */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="h-full w-full" style={{
          backgroundImage: `linear-gradient(to right, #0a0a0a 1px, transparent 1px), linear-gradient(to bottom, #0a0a0a 1px, transparent 1px)`,
          backgroundSize: '80px 80px'
        }} />
      </div>

      <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center min-h-screen py-32">
          {/* Left Content */}
          <div className="lg:col-span-7 space-y-8">
            {/* Label */}
            <p ref={labelRef} className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-medium">
              Technology & Infrastructure Leader
            </p>

            {/* Headings */}
            <div className="space-y-4 overflow-hidden">
              <div className="overflow-hidden">
                <h1 ref={headingRef} className="font-heading text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold text-foreground leading-[0.9] tracking-tight">
                  JÉRÉMIE
                </h1>
              </div>
              <div className="overflow-hidden">
                <h1 ref={heading2Ref} className="font-heading text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold text-foreground leading-[0.9] tracking-tight">
                  LICTEVOET
                </h1>
              </div>
            </div>

            {/* Subtitle */}
            <p ref={subtitleRef} className="text-lg text-steel font-medium tracking-wide">
              Technology Executive • Infrastructure Leader • Entrepreneur
            </p>

            {/* Description */}
            <p ref={descRef} className="text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed">
              Technology executive with 20+ years of experience designing, deploying and operating 
              complex technical infrastructures across Europe, Africa, Australia, the Middle East and Southeast Asia.
              Founder of FIRST Group, building technology-driven real estate and operational infrastructure in Bali.
            </p>

            {/* CTA */}
            <div ref={ctaRef} className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button
                onClick={() => scrollToSection("#contact")}
                data-testid="hero-cta-work"
                className="group bg-foreground text-background hover:bg-foreground/90 px-8 py-6 text-sm uppercase tracking-widest font-medium"
              >
                Work With Me
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                onClick={() => scrollToSection("#experience")}
                variant="outline"
                data-testid="hero-cta-explore"
                className="border-foreground text-foreground hover:bg-foreground hover:text-background px-8 py-6 text-sm uppercase tracking-widest font-medium"
              >
                View Experience
              </Button>
            </div>
          </div>

          {/* Right Image */}
          <div className="lg:col-span-5 relative hidden lg:block">
            <div className="relative aspect-[3/4] overflow-hidden">
              {/* Reveal Overlay */}
              <div 
                ref={overlayRef}
                className="absolute inset-0 bg-background z-10 origin-top"
              />
              {/* Image */}
              <div className="absolute inset-0 bg-concrete overflow-hidden">
                <img
                  ref={imageRef}
                  src="/images/hero.jpeg"
                  alt="Jérémie Lictevoet"
                  className="w-full h-full object-cover grayscale contrast-110 scale-125"
                  style={{ objectPosition: "50% 20%" }}
                />
              </div>
              {/* Border */}
              <div className="absolute -bottom-4 -right-4 w-full h-full border border-foreground/20 -z-10" />
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 cursor-pointer opacity-0 animate-[fadeIn_0.5s_ease_2s_forwards]"
          onClick={() => scrollToSection("#about")}
        >
          <span className="text-xs uppercase tracking-widest text-muted-foreground">Scroll</span>
          <ChevronDown className="w-4 h-4 text-muted-foreground animate-bounce" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
